import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';

import PublicHeader from './components/PublicHeader';

const PricingPublic = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Feature explanations for tooltips
  const featureExplanations = {
    'AI conversations limit': 'Maximum number of automated AI-powered conversations with customers per month',
    'AI driven conversations': 'Full automated conversations handled by AI from start to finish each month',
    'AI Conversation Responses': 'Number of AI-generated responses to customer inquiries per month',
    'Knowledge base documents': 'Number of documents (PDFs, text files) you can upload to train your AI',
    'Web indexing': 'Automatically index and learn from your website content',
    'Web indexing pages': 'Number of web pages that can be indexed and used by AI',
    'Web Crawling': 'Advanced automated crawling of your entire website for comprehensive knowledge extraction',
    'Instagram, Messenger Integration': 'Connect your Instagram and Facebook Messenger accounts for automated responses',
    'All channels': 'Support across WhatsApp, Web Chat, Email, Instagram, Facebook Messenger, and Telegram',
    'PDF Storage': 'Total storage space for uploaded PDF documents and files',
    'trial period': 'Free trial period to test all features before commitment',
    'No Support': 'Community support only, no dedicated technical assistance',
    'Manual Content Refresh': 'Manually trigger updates when your content changes',
    'Advanced Content Ingestion': 'Enhanced methods to import and process various content types',
    'Auto content ingestion': 'Automatically detects and imports new content without manual intervention',
    'Custom Instructions': 'Customize how AI responds by providing specific instructions and personality traits',
    'Online Installation support': 'Live video support session to help you set up and configure your AI assistant',
  };

  // Helper function to get tooltip for a feature
  const getFeatureTooltip = (feature) => {
    for (const [key, explanation] of Object.entries(featureExplanations)) {
      if (feature.toLowerCase().includes(key.toLowerCase())) {
        return explanation;
      }
    }
    return feature; // Fallback to the feature itself if no explanation found
  };

  // AI Automation Packages
  const aiPackages = [
    {
      name: 'Free',
      description: 'AI Customer Service agent',
      monthlyPrice: 0,
      isPopular: false,
      features: [
        '100 Comturk AI messaging limit',
        '20 Knowledge base documents',
        'Web indexing',
        'Instagram, Messenger Integration',
        '5 MB PDF Storage',
        'Free Forever',
        'No Support',
      ],
      cta: 'Start for Free',
      ctaVariant: 'outlined',
    },
    {
      name: 'Starter',
      description: 'For individuals and small teams',
      monthlyPrice: 14,
      isPopular: true,
      features: [
        '2000 Comturk AI messaging limit',
        '100 Knowledge base documents',
        'Web indexing',
        'All channels: WhatsApp, Web, Email, Instagram, Messenger, Telegram',
        '100 MB PDF Storage',
        'Manual Content Refresh',
      ],
      cta: 'Get Started',
      ctaVariant: 'contained',
    },
    {
      name: 'Business',
      description: 'For growing teams/active projects',
      monthlyPrice: 89,
      isPopular: false,
      features: [
        '6000 Comturk AI messaging limit',
        '500 Knowledge base documents',
        'Web indexing pages',
        'All channels: WhatsApp, Web, Email, Instagram, Messenger, Telegram',
        '250 MB PDF Storage',
        'Advanced Content Ingestion',
        'Manual content refresh',
        'Custom Instructions to train AI',
        '2 Hour Online Installation support',
      ],
      cta: 'Get Started',
      ctaVariant: 'contained',
    },
    {
      name: 'Enterprise',
      description: 'Organizations with advanced requirements',
      monthlyPrice: 499,
      isPopular: false,
      features: [
        '40000 Comturk AI messaging limit',
        '2000 Knowledge base documents',
        'Web Crawling',
        'All channels: WhatsApp, Web, Email, Instagram, Messenger, Telegram',
        '500 MB PDF Storage',
        'Advanced Content Ingestion',
        'Auto content ingestion',
        'Custom Instructions to train AI',
        '3 times 2 Hour each online installation setup',
      ],
      cta: 'Get Started',
      ctaVariant: 'contained',
    },
    {
      name: 'Premium',
      description: 'Partner with our team for guaranteed ongoing AI deployment, expert insights, and custom solutions at volume.',
      isPopular: false,
      features: [
        'Custom Comturk AI messaging limit',
        'Custom Knowledge base documents',
        'Web Crawling',
        'All channels: WhatsApp, Web, Email, Instagram, Messenger, Telegram',
        'Custom PDF Storage',
        'Advanced Content Ingestion',
        'Auto content ingestion',
        'Custom Instructions to train AI',
        'Custom Integration with your CRM, ERP',
        'Custom installation setup',
      ],
      cta: 'Contact Sales',
      ctaVariant: 'contained',
    },
  ];

  // FAQ Data
  const faqs = [
    {
      question: 'What counts as an "AI conversation"?',
      answer: 'A full conversation thread with a customer, from initial greeting to resolution. Each unique customer interaction counts as one conversation, regardless of how many messages are exchanged.',
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes! You can upgrade or downgrade at any time. Upgrades take effect immediately, while downgrades take effect at the end of your current billing period.',
    },
    {
      question: 'Do you offer refunds?',
      answer: 'We offer a 14-day money-back guarantee for all paid plans. If you\'re not satisfied, contact us for a full refund within the first 14 days.',
    },
    {
      question: 'What channels require special setup?',
      answer: 'WhatsApp integration requires a Meta Business account. Instagram integration requires an open Instagram business account. All other channels work out of the box.',
    },
    {
      question: 'What is Live Human Agent support?',
      answer: 'Live Agent allows your team members to take over conversations from AI and respond manually. Perfect for complex queries that need human touch. Priced at $19/seat/month.',
    },
    {
      question: 'How does billing work?',
      answer: 'You will be billed monthly. You can cancel anytime and your plan will remain active until the end of your billing period.',
    },
  ];

  const handleSelectPlan = (planName) => {
    if (planName === 'Enterprise' || planName === 'Premium') {
      // Redirect to contact or email
      window.location.href = 'mailto:sales@comturk.com?subject=' + encodeURIComponent(planName + ' Plan Inquiry');
    } else {
      // Go to signup with plan pre-selected
      navigate(`/signup?plan=${planName.toLowerCase()}`);
    }
  };

  const getPrice = (pkg) => {
    return pkg.monthlyPrice;
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Box sx={{ py: 8, flexGrow: 1 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box textAlign="center" mb={6}>
            <Typography variant="h6" color="text.secondary" mb={4}>
              Choose the perfect plan for your business. Always know what you'll pay.
            </Typography>
          </Box>

          {/* AI Automation Packages Section */}
          <Box mb={8}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
              <SmartToyIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" fontWeight="bold">
                AI Automation Packages
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
              All packages include automated AI-generated responses from your knowledge base
            </Typography>

            <Grid container spacing={3} alignItems="stretch">
              {aiPackages.map((pkg, index) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                  <Card
                    elevation={pkg.isPopular ? 8 : 2}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: pkg.isPopular ? `2px solid ${theme.palette.primary.main}` : 'none',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6,
                      }
                    }}
                  >
                    {pkg.isPopular && (
                      <Chip
                        icon={<StarIcon />}
                        label="Most Popular"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          fontWeight: 'bold'
                        }}
                      />
                    )}

                    <CardContent sx={{ flexGrow: 1, pt: pkg.isPopular ? 5 : 3 }}>
                      <Typography variant="h5" gutterBottom fontWeight="bold">
                        {pkg.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ minHeight: 40 }}>
                        {pkg.description}
                      </Typography>

                      <Box my={3}>
                        <Box display="flex" alignItems="baseline">
                          <Typography variant="h3" fontWeight="bold" color="primary">
                            ${getPrice(pkg)}
                          </Typography>
                          {pkg.monthlyPrice > 0 && (
                            <Typography variant="body1" color="text.secondary" ml={1}>
                              /month
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <List dense>
                        {pkg.features.map((feature, idx) => (
                          <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                            <Tooltip title={getFeatureTooltip(feature)} arrow placement="top">
                              <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                                <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant={pkg.ctaVariant}
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={() => handleSelectPlan(pkg.name)}
                        sx={{ py: 1.5 }}
                      >
                        {pkg.cta}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Live Human Agent Section */}
          <Box mb={8}>
            <Paper elevation={3} sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                <PeopleIcon sx={{ mr: 1, fontSize: 32, color: 'secondary.main' }} />
                <Typography variant="h4" fontWeight="bold">
                  Live Human Agent Support
                </Typography>
              </Box>

              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" paragraph>
                    Need human support for complex queries? Add live agents to your team who can take over from AI when needed.
                  </Typography>
                  <List>
                    <ListItem disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Seamless handoff from AI to human" />
                      <Tooltip title="Transfer conversations from AI to live agents instantly when human expertise is needed" arrow placement="top">
                        <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                    <ListItem disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary="All channels: WhatsApp, Web, Email, Instagram, Messenger, Telegram" />
                      <Tooltip title="Live agents can respond across all integrated communication channels from one unified dashboard" arrow placement="top">
                        <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                    <ListItem disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Real-time chat dashboard" />
                      <Tooltip title="Monitor and respond to customer conversations in real-time with an intuitive interface" arrow placement="top">
                        <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                    <ListItem disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Conversation history and context" />
                      <Tooltip title="Access complete conversation history and AI interactions for context before responding" arrow placement="top">
                        <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card elevation={4} sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h2" fontWeight="bold" gutterBottom>
                        $19
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        per seat / month
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                        Billed monthly • Add or remove seats anytime
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        sx={{ mt: 3, py: 1.5, bgcolor: 'white', color: 'secondary.main', '&:hover': { bgcolor: 'grey.100' } }}
                        onClick={() => navigate('/signup?addon=live-agent')}
                      >
                        Add Live Agents
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Additional Notes */}
          <Box mb={6} textAlign="center">

            <Typography variant="body2" color="text.secondary">
              • WhatsApp Integration requires Meta business account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Instagram Integration requires open Instagram business account
            </Typography>
          </Box>

          {/* FAQ Section */}
          <Box mb={8}>
            <Typography variant="h4" fontWeight="bold" textAlign="center" mb={4}>
              Frequently Asked Questions
            </Typography>

            {faqs.map((faq, index) => (
              <Accordion key={index} elevation={1}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="medium">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* CTA Section */}
          <Box textAlign="center" py={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Ready to transform your customer service?
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              Start with our free plan. No credit card required.
            </Typography>
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{ px: 4, py: 1.5 }}
              >
                Start for Free
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                href="mailto:sales@comturk.com"
                sx={{ px: 4, py: 1.5 }}
              >
                Contact Sales
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPublic;

